import { useState, useCallback, useRef, useEffect } from "react";
import { formatFileSize } from "@/lib/formatFileSize";
import { ToolContainer, FileDropZone, DownloadButton, ResultPreview } from "@/components/ToolLayout";
import { BatchRunner } from "@/components/BatchRunner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useFreemium } from "@/hooks/useFreemium";
import { loadFFmpeg, fetchFile, checkFileSize, ffmpegOutputToBlob, resetFFmpegEngine } from "@/lib/ffmpegEngine";
import { convertAudioWithPlan, AUDIO_FORMAT_OPTIONS, AUDIO_QUALITY_LABELS, type AudioQuality, matchQualityLabel, convertPlan, describeConversion, convertedName } from "./operations";
import { probeAudioFile } from "./audioFile";
import { AudioProcessingProgress, showAudioError, FileSizeNotice, UNREADABLE_AUDIO_MESSAGE } from "./uiHelpers";
import type { FFmpeg } from "@ffmpeg/ffmpeg";

const TOOL_INFO_CONTENT = {
  what: `Converts an audio file between common formats (MP3, WAV, OGG, M4A, FLAC), since different platforms and devices expect different audio formats. Runs entirely in your browser — the audio never leaves your device.`,
  how: `Upload your audio file (a file that is not audio is turned away straight away, and nothing is used from your trial), choose the format you want to convert it to, and download the converted file once processing finishes. To convert a whole folder, choose Several files and drop them all: they are converted with the same settings and downloaded as one ZIP. "Same as original" keeps a lossy file's bitrate, and uses 192 kbps for a lossless (WAV, FLAC) source. Use Convert again to turn the same file into another format without re-uploading.`,
  tip: `MP3 is the safest default for the widest compatibility. WAV and FLAC are uncompressed or losslessly compressed, so they sound identical to the source but produce much larger files — use those when quality matters more than size, like archiving a master recording.`,
};

const LOSSY = ["mp3", "ogg", "m4a"];

export default function AudioConverter() {
  const [mode, setMode] = useState<"one" | "many">("one");
  const [file, setFile] = useState<File | null>(null);
  const [format, setFormat] = useState("mp3");
  const [quality, setQuality] = useState<AudioQuality | "match">("match");
  const [duration, setDuration] = useState<number | null>(null);
  const [checking, setChecking] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [summary, setSummary] = useState("");
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [result, setResult] = useState<Blob | null>(null);
  const { isPro, isLoading, isAdmin } = useFreemium();
  const ffmpegRef = useRef<FFmpeg | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const handleFiles = useCallback((files: File[]) => {
    const f = files[0]; if (!f) return;
    setFile(f); setResult(null); setSummary(""); setDuration(null); setLoadFailed(false); setChecking(true);
    // The same question the Audio Trimmer asks when a file loads: can this be read as audio at all? Costs no trial use.
    probeAudioFile(f).then((p) => {
      if (p.readable) setDuration(p.duration); else setLoadFailed(true);
    }).finally(() => setChecking(false));
  }, []);

  const lossy = LOSSY.includes(format);
  const plan = file ? convertPlan(file, format, quality, duration) : null;
  const kbps = plan?.kbps ?? null;
  const estimate = file && duration && lossy && kbps ? (kbps * 1000 * duration) / 8 : null;
  useEffect(() => { setResult(null); }, [quality]);

  const sizeCheck = file ? checkFileSize([file], isPro || isLoading, isAdmin) : null;

  const stop = () => {
    abortRef.current?.abort();
    if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
  };

  const convert = async () => {
    if (!file || loadFailed) return;
    setProcessing(true);
    setProgress(0);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setStatusMessage("Loading audio engine...");
      const ffmpeg = await loadFFmpeg((msg) => setStatusMessage(msg));
      ffmpegRef.current = ffmpeg;

      setStatusMessage("Reading audio file...");
      const bytes = await fetchFile(file);

      const opt = AUDIO_FORMAT_OPTIONS.find(f => f.value === format)!;
      setStatusMessage(`Converting to ${opt.label}...`);
      const data = await convertAudioWithPlan(ffmpeg, file, bytes, format, quality, duration, setProgress, controller.signal);

      const outBlob = ffmpegOutputToBlob(data, opt.mime);
      setResult(outBlob);
      setSummary(describeConversion(file, outBlob.size, format, quality, duration, formatFileSize));
      toast.success("Audio converted!");
    } catch (err) {
      showAudioError(err);
    } finally {
      if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
      setProcessing(false);
    }
  };

  /** One file of a batch: its own check, its own bitrate (for "same as original"), its own engine run. A bad file fails alone. */
  const convertOne = async (f: File, signal?: AbortSignal) => {
    const probe = await probeAudioFile(f);
    if (!probe.readable) throw new Error("Couldn't read this file. It may be damaged or not audio.");
    const opt = AUDIO_FORMAT_OPTIONS.find((o) => o.value === format)!;
    const ffmpeg = await loadFFmpeg();
    try {
      const data = await convertAudioWithPlan(ffmpeg, f, await fetchFile(f), format, quality, probe.duration, () => {}, signal);
      return { blob: ffmpegOutputToBlob(data, opt.mime), fileName: convertedName(f.name, format) };
    } finally {
      resetFFmpegEngine(ffmpeg);
    }
  };

  const settings = (
    <div className="space-y-4">
      <div><Label>Output Format</Label>
        <Select value={format} onValueChange={(v) => { setFormat(v); setResult(null); }}>
          <SelectTrigger className="w-40" aria-label="Output format"><SelectValue /></SelectTrigger>
          <SelectContent>
            {AUDIO_FORMAT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {lossy && (
        <div><Label>Quality</Label>
          <div className="flex flex-wrap gap-2 mt-1" role="group" aria-label="Quality">
            {(["match", ...Object.keys(AUDIO_QUALITY_LABELS)] as (AudioQuality | "match")[]).map((q) => (
              <Button key={q} size="sm" variant={quality === q ? "default" : "outline"} aria-pressed={quality === q} onClick={() => { setQuality(q); setResult(null); }}>{q === "match" ? (mode === "one" && file ? matchQualityLabel(file, duration) : "Same as original (each file's own bitrate; lossless sources use 192 kbps)") : AUDIO_QUALITY_LABELS[q]}</Button>
            ))}
          </div>
          {mode === "one" && estimate && <p className="text-xs text-muted-foreground mt-1" data-testid="convert-estimate">About {kbps} kbps · estimated size {formatFileSize(estimate)}</p>}
        </div>
      )}
    </div>
  );

  const modeToggle = (
    <div className="flex items-center gap-2 mb-4" role="radiogroup" aria-label="How many files">
      {([["one", "One file"], ["many", "Several files (ZIP)"]] as const).map(([m, label]) => (
        <Button key={m} size="sm" variant={mode === m ? "default" : "outline"} role="radio" aria-checked={mode === m} disabled={processing} onClick={() => setMode(m)}>{label}</Button>
      ))}
    </div>
  );

  if (mode === "many") {
    return (
      <ToolContainer>
        {modeToggle}
        <BatchRunner accept="audio/*" noun="audio" process={convertOne} zipName="converted-audio.zip" runLabel="Convert all" maxSizeMb={500}>
          {settings}
        </BatchRunner>
        <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
      </ToolContainer>
    );
  }

  return (
    <ToolContainer>
      {!file && modeToggle}
      {!file ? (
        <FileDropZone gateAtDownload accept="audio/*" maxSize={500} onFiles={handleFiles} label="Drop an audio file to convert" description="Convert between audio formats" />
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-card"><p className="text-sm font-medium">{file.name}</p></div>
          {sizeCheck && <FileSizeNotice check={sizeCheck} isPro={isPro} />}
          {loadFailed && (
            <div role="alert" data-testid="convert-load-error" className="p-3 rounded-lg border border-destructive/50 text-sm text-destructive flex items-center justify-between gap-3">
              <span>{UNREADABLE_AUDIO_MESSAGE}</span>
              <Button size="sm" variant="outline" onClick={() => { setFile(null); setLoadFailed(false); }}>Choose another</Button>
            </div>
          )}
          {!loadFailed && settings}
          {processing && <AudioProcessingProgress progress={progress} statusMessage={statusMessage} onStop={stop} />}
          {result && summary && <p className="text-sm" data-testid="convert-summary">{summary}</p>}
          {result && <ResultPreview blob={result} />}
          <div className="flex flex-wrap gap-3">
            {result ? (
              <>
                <DownloadButton blob={result} fileName={convertedName(file.name, format)} />
                <Button variant="outline" onClick={() => { setResult(null); setSummary(""); }} data-testid="convert-again">Convert again</Button>
                <Button variant="outline" onClick={() => { setFile(null); setResult(null); }}>New file</Button>
              </>
            ) : (
              <>
                <Button onClick={convert} disabled={processing || checking || loadFailed || sizeCheck?.severity !== "ok"}>{processing ? "Converting..." : checking ? "Checking file..." : "Convert"}</Button>
                <Button variant="outline" onClick={() => setFile(null)} disabled={processing}>Reset</Button>
              </>
            )}
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
